import {
  isDuplicatesRule,
  isMandatoryRule,
  reconcileDataTypeCheckRules,
} from "@/lib/schemaBuilderHelpers";
import type { SchemaColumn, SchemaRule } from "@/types/schemaBuilder";
import { AlertTriangle, Edit2, Search, Trash2 } from "lucide-react";
import { useMemo, useState } from "react";

interface SchemaBuilderColumnsTabProps<
  TColumn extends {
    fieldId: string;
    id: string;
    sortOrder: number;
    name: string;
    dataType: string;
    formatting?: string;
    example: string;
    mandatory: boolean;
    unique: boolean;
  },
> {
  columns: TColumn[];
  rules: SchemaRule[];
  totalColumns: number;
  mandatoryColumns: number;
  uniqueColumns: number;
  dependencies: number;
  conflictCount: number;
  duplicateNameColumnIds?: Set<string>;
  isScratchSchema?: boolean;
  onAddColumn: () => void;
  onToggleMandatory: (fieldId: string) => void;
  onToggleUnique: (fieldId: string) => void;
  onEditColumn: (column: TColumn) => void;
  onDeleteColumn: (column: TColumn) => void;
}

export function SchemaBuilderColumnsTab<
  TColumn extends {
    fieldId: string;
    id: string;
    sortOrder: number;
    name: string;
    dataType: string;
    formatting?: string;
    example: string;
    mandatory: boolean;
    unique: boolean;
  },
>({
  columns,
  rules,
  totalColumns,
  mandatoryColumns,
  uniqueColumns,
  dependencies,
  conflictCount,
  duplicateNameColumnIds,
  isScratchSchema = false,
  onAddColumn,
  onToggleMandatory,
  onToggleUnique,
  onEditColumn,
  onDeleteColumn,
}: SchemaBuilderColumnsTabProps<TColumn>) {
  const [searchQuery, setSearchQuery] = useState("");
  const [showConflictsOnly, setShowConflictsOnly] = useState(false);
  const [showRulesOnly, setShowRulesOnly] = useState(false);

  // Display-only: reconciles pending valid_number/valid_date rules into the count
  // without adding them to `rules` state (the backend creates those on save).
  const displayRules = useMemo(
    () =>
      reconcileDataTypeCheckRules(
        columns as unknown as Pick<SchemaColumn, "fieldId" | "name" | "dataType" | "formatting">[],
        rules
      ),
    [columns, rules]
  );

  const columnsWithRulesCount = columns.filter((column) =>
    displayRules.some(
      (rule) => rule.columnIds.includes(column.fieldId) && (rule.scope ?? "column") === "column"
    )
  ).length;

  const filteredColumns = useMemo(() => {
    const normalizedQuery = searchQuery.trim().toLowerCase();

    return columns.filter((column) => {
      // Column-scoped rules only — shown in this tab; schema-scoped rules live in Cross Column Rules tab
      const columnRules = displayRules.filter(
        (rule) => rule.columnIds.includes(column.fieldId) && (rule.scope ?? "column") === "column"
      );
      const hasRules = columnRules.length > 0;
      // Conflict = column-scoped rule conflict or duplicate name (schema-scoped
      // conflicts live in Cross Column Rules tab)
      const hasConflict =
        columnRules.some((rule) => rule.hasConflict) ||
        (duplicateNameColumnIds?.has(column.fieldId) ?? false);

      const matchesSearch =
        normalizedQuery.length === 0 ||
        column.name.toLowerCase().includes(normalizedQuery) ||
        column.dataType.toLowerCase().includes(normalizedQuery) ||
        (column.formatting ?? "").toLowerCase().includes(normalizedQuery) ||
        column.example.toLowerCase().includes(normalizedQuery);

      return matchesSearch && (!showRulesOnly || hasRules) && (!showConflictsOnly || hasConflict);
    });
  }, [
    columns,
    displayRules,
    searchQuery,
    showRulesOnly,
    showConflictsOnly,
    duplicateNameColumnIds,
  ]);

  return (
    <>
      <div className="mb-6 max-w-[1100px] text-sm leading-6 text-[var(--color-text-primary)]">
        <p>
          {isScratchSchema
            ? "Use this page to create columns and set desired formatting and data types. In order to ensure that schema rules function correctly, we recommend building out all necessary columns first before proceeding to more complex steps like rule configuration."
            : "Here you can review the columns, data types, and data formatting that were parsed during your upload."}
        </p>
        <ul className="mt-2 list-disc space-y-1 pl-5">
          <li>
            <span className="font-medium">Column-specific rules</span>: click the edit icon on a
            column to open its settings drawer and configure rules for that column.
          </li>
          <li>
            <span className="font-medium">Cross-column rules</span> (rules that compare or relate
            values across two or more columns): go to the{" "}
            <span className="font-medium">Cross Column Rules</span> tab above.
          </li>
        </ul>
      </div>

      <div className="mb-4 flex flex-col gap-4 xl:flex-row xl:items-center xl:justify-between">
        <div className="flex items-center gap-3">
          <div className="flex h-9 w-[240px] shrink-0 items-center gap-2 rounded-md border border-[var(--color-border)] bg-white px-3">
            <Search className="h-4 w-4 text-[var(--color-text-tertiary)]" />
            <input
              value={searchQuery}
              onChange={(event) => setSearchQuery(event.target.value)}
              placeholder="Search columns..."
              className="w-full text-sm outline-none placeholder:text-[var(--color-text-tertiary)]"
            />
          </div>

          <button
            type="button"
            onClick={() => setShowConflictsOnly((prev) => !prev)}
            className={`h-9 whitespace-nowrap rounded-md border px-4 text-sm transition ${
              showConflictsOnly
                ? "border-[var(--color-action-primary)] bg-green-50 text-[var(--color-action-primary)]"
                : "border-[var(--color-border)] text-[var(--color-text-secondary)] hover:bg-[var(--color-bg-secondary)]"
            }`}
          >
            Has Conflicts ({conflictCount})
          </button>

          <button
            type="button"
            onClick={() => setShowRulesOnly((prev) => !prev)}
            className={`h-9 whitespace-nowrap rounded-md border px-4 text-sm transition ${
              showRulesOnly
                ? "border-[var(--color-action-primary)] bg-green-50 text-[var(--color-action-primary)]"
                : "border-[var(--color-border)] text-[var(--color-text-secondary)] hover:bg-[var(--color-bg-secondary)]"
            }`}
          >
            Has Rules ({columnsWithRulesCount})
          </button>
        </div>

        <div className="flex items-center gap-6">
          <div className="flex gap-5 text-sm text-[var(--color-text-secondary)]">
            <span className="whitespace-nowrap">{totalColumns} columns</span>
            <span className="whitespace-nowrap">{mandatoryColumns} mandatory</span>
            <span className="whitespace-nowrap">{uniqueColumns} unique</span>
            <span className="whitespace-nowrap">
              {displayRules.filter((r) => (r.scope ?? "column") === "column").length} rules
            </span>
            <span className="whitespace-nowrap">{dependencies} dependencies</span>
          </div>

          <button
            type="button"
            onClick={onAddColumn}
            className="shrink-0 whitespace-nowrap rounded-md bg-[var(--color-action-primary)] px-5 py-2 text-sm font-semibold text-white transition hover:bg-[var(--color-action-primary-hover)]"
          >
            Add Column
          </button>
        </div>
      </div>

      <div className="overflow-hidden rounded-md border border-[var(--color-border-light)] bg-white shadow-sm">
        <div className="max-h-[calc(100vh-360px)] overflow-auto">
          <table className="w-full min-w-[1000px] text-left text-sm">
            <thead className="sticky top-0 z-10 bg-[var(--color-bg-secondary)] text-xs font-semibold text-[var(--color-text-primary)]">
              <tr>
                <th className="px-5 py-3">Sort #</th>
                <th className="px-5 py-3">Column Name</th>
                <th className="px-5 py-3">Data Type</th>
                <th className="px-5 py-3">Formatting</th>
                <th className="px-5 py-3">Rules</th>
                <th className="px-5 py-3">Mandatory</th>
                <th className="px-5 py-3">Unique</th>
                <th className="px-5 py-3">Actions</th>
              </tr>
            </thead>

            <tbody>
              {filteredColumns.map((column) => {
                const columnRules = displayRules.filter(
                  (rule) =>
                    rule.columnIds.includes(column.fieldId) && (rule.scope ?? "column") === "column"
                );
                const conflictingRule = columnRules.find((rule) => rule.hasConflict);
                const hasRuleConflict = Boolean(conflictingRule);
                const hasDuplicateName = duplicateNameColumnIds?.has(column.fieldId) ?? false;
                const hasRowWarning = hasRuleConflict || hasDuplicateName;

                return (
                  <tr
                    key={column.fieldId}
                    className={`border-t border-[var(--color-border-light)] ${hasRowWarning ? "bg-amber-50" : ""}`}
                  >
                    <td className="px-5 py-4">{column.sortOrder}</td>
                    <td className="px-5 py-4">
                      <div className="flex items-center gap-2">
                        {column.name}
                        {hasRuleConflict && (
                          <span
                            title={
                              conflictingRule?.conflictReason ??
                              "This column has a rule that conflicts with another rule"
                            }
                            className="flex items-center gap-1 rounded bg-amber-100 px-1.5 py-0.5 text-xs font-medium text-amber-700"
                          >
                            <AlertTriangle className="h-3 w-3" />
                            Rule conflict
                          </span>
                        )}
                        {hasDuplicateName && (
                          <span
                            title="Another column already uses this name"
                            className="flex items-center gap-1 rounded bg-amber-100 px-1.5 py-0.5 text-xs font-medium text-amber-700"
                          >
                            <AlertTriangle className="h-3 w-3" />
                            Duplicate name
                          </span>
                        )}
                      </div>
                    </td>
                    <td className="px-5 py-4">{column.dataType}</td>
                    <td className="px-5 py-4">
                      <div>{column.formatting || "---"}</div>
                      <div className="mt-1 text-xs text-[var(--color-text-tertiary)]">
                        ex. {column.example}
                      </div>
                    </td>
                    <td className="px-5 py-4">
                      {columnRules.length > 0 ? `${columnRules.length} rule(s)` : "None"}
                    </td>
                    <td className="px-5 py-4">
                      <input
                        type="checkbox"
                        checked={column.mandatory || columnRules.some(isMandatoryRule)}
                        onChange={() => onToggleMandatory(column.fieldId)}
                        className="h-4 w-4 cursor-pointer accent-[var(--color-action-primary)]"
                      />
                    </td>
                    <td className="px-5 py-4">
                      <input
                        type="checkbox"
                        checked={column.unique || columnRules.some(isDuplicatesRule)}
                        onChange={() => onToggleUnique(column.fieldId)}
                        className="h-4 w-4 cursor-pointer accent-[var(--color-action-primary)]"
                      />
                    </td>
                    <td className="px-5 py-4">
                      <div className="flex gap-3">
                        <button
                          type="button"
                          onClick={() => onEditColumn(column)}
                          className="rounded border border-[var(--color-border)] p-1.5 transition hover:bg-[var(--color-bg-secondary)]"
                        >
                          <Edit2 className="h-4 w-4" />
                        </button>

                        <button
                          type="button"
                          onClick={() => onDeleteColumn(column)}
                          className="rounded border border-red-300 p-1.5 text-red-500 transition hover:bg-red-50"
                        >
                          <Trash2 className="h-4 w-4" />
                        </button>
                      </div>
                    </td>
                  </tr>
                );
              })}

              {filteredColumns.length === 0 && (
                <tr>
                  <td
                    colSpan={8}
                    className="h-[320px] px-5 py-10 text-center text-sm text-[var(--color-text-secondary)]"
                  >
                    {columns.length === 0 ? (
                      <div className="flex flex-col items-center justify-center gap-2">
                        <p>Your schema has no columns yet.</p>
                        <p className="text-xs text-[var(--color-text-tertiary)]">
                          Click the "Add Column" button above to begin configuring your schema.
                        </p>
                      </div>
                    ) : (
                      "No columns match the current filters."
                    )}
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      </div>
    </>
  );
}
